import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

export type StatisticsPeriod = { from: string; to: string } | null;

export default function useUserStatisticsPeriod(active: boolean) {
  const [period, setPeriod] = useState<StatisticsPeriod>(null);
  const [revision, setRevision] = useState(0);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [details, setDetails] = useState<Record<number, any>>({});
  const [loadingDetails, setLoadingDetails] = useState<Record<number, boolean>>({});
  const [detailErrors, setDetailErrors] = useState<Record<number, string>>({});
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const scope = useRef(0);
  const params = period ? { date_from: period.from, date_to: period.to } : {};

  const changePeriod = useCallback((next: StatisticsPeriod) => {
    // Invalidate in-flight history immediately, including before the next effect.
    scope.current += 1;
    setPeriod(next);
    setRevision(value => value + 1);
    setUsers([]);
    setDetails({});
    setLoadingDetails({});
    setDetailErrors({});
    setExpandedRows(new Set());
    setError('');
    setLoading(true);
  }, []);

  useEffect(() => {
    if (!active) return;
    const currentScope = scope.current;
    let cancelled = false;
    setLoading(true);
    setError('');
    api.get('/admin/statistics/by-user', { params: { ...params, limit: 5000 } })
      .then(response => {
        if (cancelled || currentScope !== scope.current) return;
        if (!Array.isArray(response.data)) throw new Error('Некорректный ответ рейтинга');
        setUsers(response.data.map(user => ({
          ...user,
          user_name: user.full_name,
          department: user.department || 'Не указан',
          position: user.position || 'Не указан',
          work_experience: user.work_experience || 'Не указан',
          region: user.region || 'Не указан',
          attempts_count: user.total_attempts,
          passed_count: user.passed_attempts,
          failed_count: user.total_attempts - user.passed_attempts,
        })).sort((a, b) => b.average_score - a.average_score || a.user_name.localeCompare(b.user_name)));
      })
      .catch(reason => {
        if (!cancelled && currentScope === scope.current) {
          setError(getApiErrorMessage(reason, 'Не удалось загрузить рейтинг'));
        }
      })
      .finally(() => {
        if (!cancelled && currentScope === scope.current) setLoading(false);
      });
    return () => { cancelled = true; scope.current += 1; };
    // Request parameters are fully determined by period; revision supports retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, period, revision]);

  const closeDetails = (userId: number) => setExpandedRows(previous => {
    const next = new Set(previous);
    next.delete(userId);
    return next;
  });

  const loadDetails = async (userId: number) => {
    setExpandedRows(previous => new Set(previous).add(userId));
    if (details[userId] || loadingDetails[userId]) return;
    const currentScope = scope.current;
    setLoadingDetails(previous => ({ ...previous, [userId]: true }));
    setDetailErrors(previous => ({ ...previous, [userId]: '' }));
    try {
      const response = await api.get(`/admin/statistics/user/${userId}/details`, { params });
      if (currentScope !== scope.current) return;
      if (response.data?.user_id !== userId || !Array.isArray(response.data?.tests)) {
        throw new Error('Некорректная история сотрудника');
      }
      setDetails(previous => ({ ...previous, [userId]: response.data }));
    } catch (reason) {
      if (currentScope === scope.current) {
        setDetailErrors(previous => ({ ...previous, [userId]: getApiErrorMessage(reason, 'Не удалось загрузить историю') }));
      }
    } finally {
      if (currentScope === scope.current) setLoadingDetails(previous => ({ ...previous, [userId]: false }));
    }
  };

  return { period, changePeriod, users, loading, error, details, loadingDetails,
    detailErrors, expandedRows, loadDetails, closeDetails };
}
